import React, { useState, useEffect, Fragment } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

import { useToast } from '../hooks/use-toast';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Clock,
  Plus,
  Edit,
  Trash,
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Users,
  FileText,
  Settings,
  BarChart3,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

import BancoHorasDashboard from './banco_horas/BancoHorasDashboard';
import AbsenteismoPanel from './banco_horas/AbsenteismoPanel';
import DivergenciasPanel from './banco_horas/DivergenciasPanel';
import EspelhoPontoPanel from './banco_horas/EspelhoPontoPanel';

const API = process.env.REACT_APP_BACKEND_URL;

// Função helper para converter horas decimais em formato HH:MM
const formatarHorasDecimal = (horasDecimal) => {
  if (horasDecimal === null || horasDecimal === undefined) return '00:00';
  
  const ehNegativo = horasDecimal < 0;
  const horasAbs = Math.abs(horasDecimal);
  
  const horas = Math.floor(horasAbs);
  const minutos = Math.round((horasAbs - horas) * 60);
  
  const horasStr = String(horas).padStart(2, '0');
  const minutosStr = String(minutos).padStart(2, '0');
  
  return ehNegativo ? `-${horasStr}:${minutosStr}` : `${horasStr}:${minutosStr}`;
};

const pathToTab = {
  '/banco-horas': 'dashboard',
  '/banco-horas/absenteismo': 'absenteismo',
  '/banco-horas/divergencias': 'divergencias',
  '/banco-horas/espelho': 'espelho',
  '/banco-horas/relatorios': 'relatorios',
  '/banco-horas/acordos': 'acordos',
  '/banco-horas/jornadas': 'jornadas',
  '/banco-horas/saldos': 'saldos',
};

const BancoHorasPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const ALL_PAGE_TABS = ['dashboard', 'absenteismo', 'divergencias', 'espelho', 'acordos', 'jornadas', 'saldos', 'relatorios'];
  const allowedTabs = ALL_PAGE_TABS;
  const [activeTab, setActiveTab] = useState(pathToTab[location.pathname] || 'dashboard');
  const { toast } = useToast();

  // Navegar quando a aba muda
  const handleTabChange = (value) => {
    setActiveTab(value);
    const tabToPath = {
      'dashboard': '/banco-horas',
      'absenteismo': '/banco-horas/absenteismo',
      'divergencias': '/banco-horas/divergencias',
      'espelho': '/banco-horas/espelho',
      'relatorios': '/banco-horas/relatorios',
      'acordos': '/banco-horas/acordos',
      'jornadas': '/banco-horas/jornadas',
      'saldos': '/banco-horas/saldos',
    };
    navigate(tabToPath[value] || '/banco-horas');
  };

  // Sincronizar com navegação direta (ex: sidebar)
  useEffect(() => {
    const tab = pathToTab[location.pathname];
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [location.pathname]);
  
  // Estados principais
  const [loading, setLoading] = useState(false);
  const [acordos, setAcordos] = useState([]);
  const [convencoes, setConvencoes] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [empresaFiltro, setEmpresaFiltro] = useState('');
  const [saldos, setSaldos] = useState([]);
  const [saldosLoading, setSaldosLoading] = useState(false);
  const [alertas, setAlertas] = useState([]);
  
  // Estados para detalhamento mensal
  const [funcionarioExpandido, setFuncionarioExpandido] = useState(null);
  const [detalhamentoMensal, setDetalhamentoMensal] = useState({});
  const [mesExpandido, setMesExpandido] = useState(null); // "funcId-mes-ano"

  // Estados para formulários
  const [showAcordoForm, setShowAcordoForm] = useState(false);
  const [showJornadaForm, setShowJornadaForm] = useState(false);
  const [showHorarioForm, setShowHorarioForm] = useState(false);
  const [editingAcordo, setEditingAcordo] = useState(null);
  const [editingJornada, setEditingJornada] = useState(null);
  const [editingHorario, setEditingHorario] = useState(null);

  // Estados de formulário - Acordo
  const [acordoForm, setAcordoForm] = useState({
    nome: '',
    descricao: '',
    convencao_coletiva_id: '',
    periodo_compensacao_meses: 6,
    periodos: [],
    limite_mensal_horas: '',
    compensacao_tipo: 'fim_periodo',
    excedente_tipo: 'pagar',
    descontar_negativo: true,
    pagar_positivo: true
  });

  // Estados de formulário - Jornada (NOVO FORMATO - Opção 1)
  const [jornadaForm, setJornadaForm] = useState({
    nome: '',
    descricao: '',
    jornada_semanal: 40.0, // Campo principal
    horarios: [
      {
        id: Date.now().toString(),
        nome: 'Horário 1',
        entrada: '08:00',
        saida: '17:00',
        intervalo_inicio: '12:00',
        intervalo_fim: '13:00',
        horas_calculadas: 8.0
      }
    ],
    dias_horarios: [
      { dia: 'segunda', horario_id: null, horas: 0 },
      { dia: 'terca', horario_id: null, horas: 0 },
      { dia: 'quarta', horario_id: null, horas: 0 },
      { dia: 'quinta', horario_id: null, horas: 0 },
      { dia: 'sexta', horario_id: null, horas: 0 },
      { dia: 'sabado', horario_id: null, horas: 0 },
      { dia: 'domingo', horario_id: null, horas: 0 }
    ],
    // Campos legados (compatibilidade)
    horas_diarias: 8.0,
    dias_semana: ['segunda', 'terca', 'quarta', 'quinta', 'sexta'],
    tolerancia_minutos: 10,
    tolerancia_acumulativa: false,
    max_marcacoes_dia: 12,
    dia_fechamento_ponto: ''
  });

  // Estados de formulário - Horário
  const [horarioForm, setHorarioForm] = useState({
    nome: '',
    entrada: '08:00',
    saida: '17:00',
    intervalo_inicio: '12:00',
    intervalo_fim: '13:00',
    jornada_id: '', // Mudado de carga_horaria_diaria para jornada_id
    dias_semana: ['segunda', 'terca', 'quarta', 'quinta', 'sexta'], // Array de dias selecionados
    ativo: true
  });

  // Função para converter jornada antiga para nova estrutura
  const converterJornadaParaNovaEstrutura = (jornada) => {
    // Se já tem a nova estrutura, retornar como está
    if (jornada.horarios && jornada.dias_horarios) {
      return jornada;
    }
    
    // Converter estrutura antiga para nova
    const horarioId = Date.now().toString();
    const horarioPadrao = {
      id: horarioId,
      nome: 'Horário 1',
      entrada: jornada.entrada || '08:00',
      saida: jornada.saida || '17:00',
      intervalo_inicio: jornada.intervalo_inicio || '12:00',
      intervalo_fim: jornada.intervalo_fim || '13:00',
      horas_calculadas: jornada.horas_diarias || 8.0
    };
    
    // Criar mapeamento de dias
    const diasSemana = jornada.dias_semana || ['segunda', 'terca', 'quarta', 'quinta', 'sexta'];
    const diasHorarios = [
      'segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'
    ].map(dia => ({
      dia: dia,
      horario_id: diasSemana.includes(dia) ? horarioId : null,
      horas: diasSemana.includes(dia) ? (jornada.horas_diarias || 8.0) : 0
    }));
    
    return {
      ...jornada,
      jornada_semanal: jornada.jornada_semanal || (jornada.horas_diarias * diasSemana.length) || 40.0,
      horarios: [horarioPadrao],
      dias_horarios: diasHorarios,
      tolerancia_minutos: jornada.tolerancia_minutos || 10,
      tolerancia_acumulativa: jornada.tolerancia_acumulativa || false,
      max_marcacoes_dia: jornada.max_marcacoes_dia || 12,
      dia_fechamento_ponto: jornada.dia_fechamento_ponto || ''
    };
  };

  // Função para calcular horas de um horário
  const calcularHorasHorario = (entrada, saida, intervaloInicio, intervaloFim) => {
    try {
      const [entradaH, entradaM] = entrada.split(':').map(Number);
      const [saidaH, saidaM] = saida.split(':').map(Number);
      
      let totalMinutos = (saidaH * 60 + saidaM) - (entradaH * 60 + entradaM);
      
      // Se negativo, passou da meia-noite
      if (totalMinutos < 0) {
        totalMinutos += 24 * 60;
      }
      
      // Subtrair intervalo
      if (intervaloInicio && intervaloFim) {
        const [intervaloInicioH, intervaloInicioM] = intervaloInicio.split(':').map(Number);
        const [intervaloFimH, intervaloFimM] = intervaloFim.split(':').map(Number);
        
        let intervaloMinutos = (intervaloFimH * 60 + intervaloFimM) - (intervaloInicioH * 60 + intervaloInicioM);
        
        if (intervaloMinutos < 0) {
          intervaloMinutos += 24 * 60;
        }
        
        totalMinutos -= intervaloMinutos;
      }
      
      return Math.round((totalMinutos / 60) * 100) / 100; // Arredondar para 2 casas decimais
    } catch (e) {
      return 0;
    }
  };

  useEffect(() => {
    if (user) {
      carregarDados();
    }
  }, [user]);

  useEffect(() => {
    if (user && funcionarios.length > 0) {
      const token = localStorage.getItem('token');
      if (!token) return;
      const headers = { Authorization: `Bearer ${token}` };
      let funcionariosFiltrados = funcionarios;
      if (empresaFiltro) {
        funcionariosFiltrados = funcionarios.filter(f => f.company_id === empresaFiltro);
      }
      carregarSaldosFuncionarios(funcionariosFiltrados, headers);
    }
  }, [empresaFiltro, funcionarios]);

  const carregarAlertas = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const params = empresaFiltro ? { empresa_id: empresaFiltro } : {};
      const response = await axios.get(`${API}/api/banco-horas/alertas-fechamento`, { headers, params });
      setAlertas(response.data.alertas || []);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    }
  };

  useEffect(() => {
    if (user) {
      carregarAlertas();
    }
  }, [user, empresaFiltro, funcionarios]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Carregar acordos, jornadas, horários, funcionários e empresas
      const [acordosRes, convencoesRes, jornadasRes, horariosRes, funcionariosRes, empresasRes] = await Promise.all([
        axios.get(`${API}/api/banco-horas/acordos`, { headers }),
        axios.get(`${API}/api/banco-horas/convencoes-disponiveis`, { headers }),
        axios.get(`${API}/api/banco-horas/jornadas`, { headers }),
        axios.get(`${API}/api/ponto/horarios-trabalho`, { headers }),
        axios.get(`${API}/api/employees?limit=0`, { headers }),
        axios.get(`${API}/api/companies`, { headers })
      ]);

      setAcordos(acordosRes.data);
      setConvencoes(convencoesRes.data);
      setJornadas(jornadasRes.data);
      setHorarios(horariosRes.data);
      setEmpresas(empresasRes.data);
      // Apenas ativos; apenas CLT (ou regime não preenchido — assume CLT)
      const ativosCLT = (funcionariosRes.data || []).filter(f => {
        const sit = (f.situacao || '').toLowerCase();
        if (sit !== 'ativo') return false;
        const reg = (f.regime || '').toLowerCase();
        if (!reg || reg === 'não informado' || reg === 'nao informado' || reg === 'clt') return true;
        return false;
      });
      setFuncionarios(ativosCLT);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do banco de horas",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const exportarSaldosCSV = () => {
    const linhas = [['Funcionário', 'Empresa', 'Acordo', 'Jornada', 'Saldo Acumulado', 'Saldo Mensal', 'Status']];
    saldos.forEach(item => {
      linhas.push([
        item.funcionario.nome || item.funcionario.name || '',
        item.empresa?.name || '',
        item.acordoNome || '',
        item.jornadaNome || '',
        item.saldo ? formatarHorasDecimal(item.saldo.saldo_acumulado_horas) : '',
        item.saldo ? formatarHorasDecimal(item.saldo.saldo_mensal_horas) : '',
        item.saldo?.status || (item.erro || 'Sem dados')
      ]);
    });
    const csv = linhas.map(l => l.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const bom = '\uFEFF';
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `saldos_banco_horas_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const carregarSaldosFuncionarios = async (funcionariosList, headers) => {
    if (!funcionariosList || funcionariosList.length === 0) {
      setSaldos([]);
      setSaldosLoading(false);
      return;
    }
    setSaldosLoading(true);
    try {
      const funcionario_ids = funcionariosList.map(f => f.id);
      const response = await axios.post(`${API}/api/banco-horas/saldos-batch`,
        { funcionario_ids },
        { headers }
      );
      const saldosMap = response.data.saldos || {};

      const resultados = funcionariosList.map(func => {
        const saldo = saldosMap[func.id];
        const empresa = empresas.find(e => e.id === func.company_id);
        const acordoNome = acordos.find(a => a.id === (func.acordo_banco_horas_id || empresa?.acordo_banco_horas_id))?.nome;
        const jornadaNome = jornadas.find(j => j.id === (func.jornada_id || empresa?.jornada_id))?.nome;
        return {
          funcionario: func,
          empresa: empresa || null,
          acordoNome,
          jornadaNome,
          herdado: !func.acordo_banco_horas_id || !func.jornada_id,
          saldo: saldo || null,
          erro: saldo ? null : 'Sem dados'
        };
      });

      setSaldos(resultados);
    } catch (error) {
      console.error('Erro ao carregar saldos:', error);
    } finally {
      setSaldosLoading(false);
    }
  };

  const calcularBancoHoras = async (funcionarioId) => {
    try {
      const token = localStorage.getItem('token');
      setLoading(true);

      const response = await axios.post(
        `${API}/api/banco-horas/calcular/${funcionarioId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast({
        title: "Sucesso!",
        description: "Banco de horas calculado com sucesso!",
        variant: "default"
      });
      
      // Recarregar dados
      carregarDados();
      
      return response.data;
    } catch (error) {
      console.error('Erro ao calcular banco de horas:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.detail || 'Erro ao calcular banco de horas',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const calcularTodos = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/banco-horas/calcular-todos`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const resultado = response.data;
      
      // Aguardar 1 segundo para garantir que o banco salvou
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Recarregar dados ANTES de mostrar toast
      await carregarDados();
      
      toast({
        title: "Sucesso!",
        description: `Cálculo concluído! ${resultado.sucessos} sucessos, ${resultado.erros} erros`,
        variant: "default"
      });
      
    } catch (error) {
      console.error('Erro ao calcular todos:', error);
      toast({
        title: "Erro",
        description: "Erro ao calcular banco de horas para todos",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // NOVO: Buscar detalhamento mensal de um funcionário
  const buscarDetalhamentoMensal = async (funcionarioId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/banco-horas/saldo-mensal/${funcionarioId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.meses) {
        setDetalhamentoMensal(prev => ({
          ...prev,
          [funcionarioId]: response.data.meses
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar detalhamento:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar o detalhamento mensal",
        variant: "destructive"
      });
    }
  };

  // Toggle expandir/colapsar funcionário
  const toggleExpandir = async (funcionarioId) => {
    if (funcionarioExpandido === funcionarioId) {
      // Se já está expandido, colapsar
      setFuncionarioExpandido(null);
    } else {
      // Expandir e buscar detalhamento
      setFuncionarioExpandido(funcionarioId);
      if (!detalhamentoMensal[funcionarioId]) {
        await buscarDetalhamentoMensal(funcionarioId);
      }
    }
  };

  // Funções para Acordos
  const salvarAcordo = async () => {
    try {
      const token = localStorage.getItem('token');
      const dados = {
        ...acordoForm,
        limite_mensal_horas: acordoForm.limite_mensal_horas ? parseFloat(acordoForm.limite_mensal_horas) : null,
        periodos: acordoForm.periodos || []
      };

      if (editingAcordo) {
        await axios.put(`${API}/api/banco-horas/acordos/${editingAcordo.id}`, dados, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Sucesso!",
          description: "Acordo atualizado com sucesso!",
          variant: "default"
        });
      } else {
        await axios.post(`${API}/api/banco-horas/acordos`, dados, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Sucesso!",
          description: "Acordo criado com sucesso!",
          variant: "default"
        });
      }

      setShowAcordoForm(false);
      setEditingAcordo(null);
      setAcordoForm({
        nome: '',
        descricao: '',
        periodo_compensacao_meses: 6,
        periodos: [
          { mes_inicial: 1, mes_final: 6 },
          { mes_inicial: 7, mes_final: 12 }
        ],
        convencao_coletiva_id: '',
        limite_mensal_horas: '',
        compensacao_tipo: 'fim_periodo',
        excedente_tipo: 'pagar',
        descontar_negativo: true,
        pagar_positivo: true
      });
      carregarDados();

    } catch (error) {
      console.error('Erro ao salvar acordo:', error);
      const errorMsg = error.response?.data?.detail || "Erro ao salvar acordo";
      toast({
        title: "Erro",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  const excluirAcordo = async (acordo) => {
    if (!window.confirm(`Tem certeza que deseja excluir o acordo "${acordo.nome}"?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/banco-horas/acordos/${acordo.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Sucesso!",
        description: "Acordo excluído com sucesso!",
        variant: "default"
      });
      
      carregarDados();
      
    } catch (error) {
      console.error('Erro ao excluir acordo:', error);
      const errorMsg = error.response?.data?.detail || "Erro ao excluir acordo";
      toast({
        title: "Erro",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  // Funções para Jornadas
  const salvarJornada = async () => {
    try {
      const token = localStorage.getItem('token');
      // Jornada sem intervalo (apenas entrada e saída): intervalo vazio vai como null
      const payloadJornada = {
        ...jornadaForm,
        dia_fechamento_ponto: jornadaForm.dia_fechamento_ponto ? Number(jornadaForm.dia_fechamento_ponto) : null,
        horarios: (jornadaForm.horarios || []).map((h) => {
          const semIntervalo = !h.intervalo_inicio || !h.intervalo_fim;
          return {
            ...h,
            intervalo_inicio: semIntervalo ? null : h.intervalo_inicio,
            intervalo_fim: semIntervalo ? null : h.intervalo_fim
          };
        })
      };

      if (editingJornada) {
        await axios.put(`${API}/api/banco-horas/jornadas/${editingJornada.id}`, payloadJornada, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Sucesso!",
          description: "Jornada atualizada com sucesso!",
          variant: "default"
        });
      } else {
        await axios.post(`${API}/api/banco-horas/jornadas`, payloadJornada, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Sucesso!",
          description: "Jornada criada com sucesso!",
          variant: "default"
        });
      }

      setShowJornadaForm(false);
      setEditingJornada(null);
      setJornadaForm({
        nome: '', 
        descricao: '', 
        horas_diarias: 8.0, 
        dias_semana: ['segunda', 'terca', 'quarta', 'quinta', 'sexta'],
        entrada: '08:00',
        saida: '17:00',
        intervalo_inicio: '12:00',
        intervalo_fim: '13:00',
        tolerancia_minutos: 10,
        tolerancia_acumulativa: false,
        max_marcacoes_dia: 12,
        dia_fechamento_ponto: ''
      });
      carregarDados();

    } catch (error) {
      console.error('Erro ao salvar jornada:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar jornada",
        variant: "destructive"
      });
    }
  };

  const excluirJornada = async (jornada) => {
    if (!window.confirm(`Tem certeza que deseja excluir a jornada "${jornada.nome}"?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/banco-horas/jornadas/${jornada.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Sucesso!",
        description: "Jornada excluída com sucesso!",
        variant: "default"
      });
      
      carregarDados();
      
    } catch (error) {
      console.error('Erro ao excluir jornada:', error);
      const errorMsg = error.response?.data?.detail || "Erro ao excluir jornada";
      toast({
        title: "Erro",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  // Funções para Horários de Trabalho
  const salvarHorario = async () => {
    try {
      const token = localStorage.getItem('token');

      if (editingHorario) {
        await axios.put(`${API}/api/ponto/horarios-trabalho/${editingHorario.id}`, horarioForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Sucesso!",
          description: "Horário atualizado com sucesso!",
          variant: "default"
        });
      } else {
        await axios.post(`${API}/api/ponto/horarios-trabalho`, horarioForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Sucesso!",
          description: "Horário criado com sucesso!",
          variant: "default"
        });
      }

      setShowHorarioForm(false);
      setEditingHorario(null);
      setHorarioForm({
        nome: '',
        entrada: '08:00',
        saida: '17:00',
        intervalo_inicio: '12:00',
        intervalo_fim: '13:00',
        jornada_id: '',
        dias_semana: ['segunda', 'terca', 'quarta', 'quinta', 'sexta'],
        ativo: true
      });
      carregarDados();

    } catch (error) {
      console.error('Erro ao salvar horário:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar horário",
        variant: "destructive"
      });
    }
  };

  const excluirHorario = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este horário?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/ponto/horarios-trabalho/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({
        title: "Sucesso!",
        description: "Horário excluído com sucesso!",
        variant: "default"
      });
      carregarDados();
    } catch (error) {
      console.error('Erro ao excluir horário:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir horário",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Banco de Horas</h1>
        <p className="text-gray-600">
          Gerencie acordos, jornadas e acompanhe os saldos dos funcionários
        </p>
        <div className="mt-4 flex items-center space-x-4">
          <div className="w-72">
            <Label htmlFor="filtro-empresa">Filtrar por Empresa</Label>
            <select
              id="filtro-empresa"
              value={empresaFiltro}
              onChange={(e) => setEmpresaFiltro(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none text-sm"
            >
              <option value="">Todas as empresas</option>
              {empresas.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList
          className="flex w-full items-center justify-start gap-1 overflow-x-auto sm:grid sm:overflow-x-visible"
          style={{gridTemplateColumns: `repeat(${allowedTabs.length}, minmax(0, 1fr))`}}
        >
          {allowedTabs.includes('dashboard') && <TabsTrigger value="dashboard" className="flex-shrink-0">Dashboard</TabsTrigger>}
          {allowedTabs.includes('absenteismo') && <TabsTrigger value="absenteismo" className="flex-shrink-0">Absenteísmo</TabsTrigger>}
          {allowedTabs.includes('divergencias') && <TabsTrigger value="divergencias" className="flex-shrink-0">Divergências</TabsTrigger>}
          {allowedTabs.includes('acordos') && <TabsTrigger value="acordos" className="flex-shrink-0">Acordos</TabsTrigger>}
          {allowedTabs.includes('jornadas') && <TabsTrigger value="jornadas" className="flex-shrink-0">Jornadas</TabsTrigger>}
          {allowedTabs.includes('saldos') && <TabsTrigger value="saldos" className="flex-shrink-0">Saldos</TabsTrigger>}
          {allowedTabs.includes('relatorios') && <TabsTrigger value="relatorios" className="flex-shrink-0">Relatórios</TabsTrigger>}
        </TabsList>

        {/* Aba Dashboard */}
        {allowedTabs.includes('dashboard') && (
        <TabsContent value="dashboard" className="space-y-6">
          <BancoHorasDashboard empresas={empresas} empresaFiltro={empresaFiltro} />
        </TabsContent>
        )}

        {/* Aba Absenteísmo */}
        {allowedTabs.includes('absenteismo') && (
        <TabsContent value="absenteismo" className="space-y-6">
          <AbsenteismoPanel empresas={empresas} empresaFiltro={empresaFiltro} />
        </TabsContent>
        )}

        {/* Aba Divergências */}
        {allowedTabs.includes('divergencias') && (
        <TabsContent value="divergencias" className="space-y-6">
          <DivergenciasPanel empresas={empresas} empresaFiltro={empresaFiltro} />
        </TabsContent>
        )}


        {/* Aba Acordos */}
        {allowedTabs.includes('acordos') && (
        <TabsContent value="acordos" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Acordos de Banco de Horas</CardTitle>
                  <CardDescription>
                    Configure os diferentes tipos de acordos sindicais
                  </CardDescription>
                </div>
                <Button onClick={() => {
                  // Inicializar com 2 períodos (padrão para 6 meses)
                  setAcordoForm({
                    nome: '',
                    descricao: '',
                    convencao_coletiva_id: '',
                    periodo_compensacao_meses: 6,
                    periodos: [
                      { mes_inicial: 1, mes_final: 6 },
                      { mes_inicial: 7, mes_final: 12 }
                    ],
                    limite_mensal_horas: ''
                  });
                  setShowAcordoForm(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Acordo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {acordos.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nenhum acordo cadastrado</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {acordos.map((acordo) => {
                    const mesesNomes = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                    return (
                      <div key={acordo.id} className="border rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{acordo.nome}</h3>
                            {acordo.descricao && (
                              <p className="text-gray-600 mt-1">{acordo.descricao}</p>
                            )}
                            <div className="flex space-x-4 mt-2 text-sm text-gray-500">
                              <span>📅 {acordo.periodo_compensacao_meses} meses</span>
                              {acordo.limite_mensal_horas && (
                                <span>⏰ Limite: {acordo.limite_mensal_horas}h/mês</span>
                              )}
                              {acordo.convencao_coletiva_nome && (
                                <span>📘 {acordo.convencao_coletiva_nome}</span>
                              )}
                            </div>
                            
                            {/* Exibir períodos configurados */}
                            {acordo.periodos && acordo.periodos.length > 0 && (
                              <div className="mt-3 space-y-1">
                                <p className="text-xs font-medium text-gray-600">Períodos de Compensação:</p>
                                <div className="flex flex-wrap gap-2">
                                  {acordo.periodos.map((periodo, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs">
                                      {mesesNomes[periodo.mes_inicial - 1]} → {mesesNomes[periodo.mes_final - 1]}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingAcordo(acordo);
                                setAcordoForm({
                                  ...acordo,
                                  periodos: acordo.periodos || []
                                });
                                setShowAcordoForm(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => excluirAcordo(acordo)}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Modal/Form para Acordo */}
          {showAcordoForm && (
            <Card>
              <CardHeader>
                <CardTitle>
                  {editingAcordo ? 'Editar Acordo' : 'Novo Acordo'}
                </CardTitle>
                <CardDescription>
                  Configure o período de compensação e os intervalos de reset do banco de horas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label htmlFor="acordo-nome">Nome do Acordo</Label>
                  <Input
                    id="acordo-nome"
                    placeholder="Ex: Acordo Sindicato Metalúrgicos"
                    value={acordoForm.nome}
                    onChange={(e) => setAcordoForm({...acordoForm, nome: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="acordo-descricao">Descrição</Label>
                  <Input
                    id="acordo-descricao"
                    placeholder="Descrição opcional do acordo"
                    value={acordoForm.descricao}
                    onChange={(e) => setAcordoForm({...acordoForm, descricao: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="acordo-convencao">Convenção Coletiva Vinculada</Label>
                  <select
                    id="acordo-convencao"
                    value={acordoForm.convencao_coletiva_id || ''}
                    onChange={(e) => setAcordoForm({ ...acordoForm, convencao_coletiva_id: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Nenhuma convenção vinculada</option>
                    {convencoes.map((convencao) => (
                      <option key={convencao.id} value={convencao.id}>
                        {convencao.nome}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-1">
                    Quando definida, novas vinculações incompatíveis entre acordo e convenção serão bloqueadas.
                  </p>
                </div>

                <div>
                  <Label htmlFor="acordo-periodo">Período de Compensação (meses)</Label>
                  <select
                    id="acordo-periodo"
                    value={acordoForm.periodo_compensacao_meses}
                    onChange={(e) => {
                      const meses = parseInt(e.target.value);
                      // Calcular quantidade de períodos necessários
                      let qtdPeriodos = 0;
                      if (meses === 3) qtdPeriodos = 4;
                      else if (meses === 4) qtdPeriodos = 3;
                      else if (meses === 6) qtdPeriodos = 2;
                      else if (meses === 12) qtdPeriodos = 1;
                      
                      // Criar períodos vazios
                      const novosPeriodos = Array(qtdPeriodos).fill(null).map(() => ({
                        mes_inicial: 1,
                        mes_final: meses
                      }));
                      
                      setAcordoForm({
                        ...acordoForm, 
                        periodo_compensacao_meses: meses,
                        periodos: novosPeriodos
                      });
                    }}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none"
                  >
                    <option value={3}>3 meses (4 períodos)</option>
                    <option value={4}>4 meses (3 períodos)</option>
                    <option value={6}>6 meses (2 períodos)</option>
                    <option value={12}>12 meses (1 período)</option>
                  </select>
                </div>

                {/* Seção de Períodos de Compensação */}
                {acordoForm.periodos && acordoForm.periodos.length > 0 && (
                  <div className="border-t pt-4">
                    <Label className="text-base font-semibold mb-3 block">
                      Períodos de Compensação
                    </Label>
                    <p className="text-sm text-gray-600 mb-4">
                      Defina os meses de início e fim de cada período. Ao final de cada período, o banco de horas será zerado.
                    </p>
                    
                    <div className="space-y-4">
                      {acordoForm.periodos.map((periodo, index) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg border">
                          <h4 className="font-medium mb-3 text-sm text-gray-700">
                            Período {index + 1}
                          </h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor={`periodo-${index}-inicio`} className="text-sm">
                                Mês Inicial
                              </Label>
                              <select
                                id={`periodo-${index}-inicio`}
                                value={periodo.mes_inicial}
                                onChange={(e) => {
                                  const novosPeriodos = [...acordoForm.periodos];
                                  novosPeriodos[index] = {
                                    ...novosPeriodos[index],
                                    mes_inicial: parseInt(e.target.value)
                                  };
                                  setAcordoForm({...acordoForm, periodos: novosPeriodos});
                                }}
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none"
                              >
                                <option value={1}>Janeiro</option>
                                <option value={2}>Fevereiro</option>
                                <option value={3}>Março</option>
                                <option value={4}>Abril</option>
                                <option value={5}>Maio</option>
                                <option value={6}>Junho</option>
                                <option value={7}>Julho</option>
                                <option value={8}>Agosto</option>
                                <option value={9}>Setembro</option>
                                <option value={10}>Outubro</option>
                                <option value={11}>Novembro</option>
                                <option value={12}>Dezembro</option>
                              </select>
                            </div>
                            <div>
                              <Label htmlFor={`periodo-${index}-fim`} className="text-sm">
                                Mês Final
                              </Label>
                              <select
                                id={`periodo-${index}-fim`}
                                value={periodo.mes_final}
                                onChange={(e) => {
                                  const novosPeriodos = [...acordoForm.periodos];
                                  novosPeriodos[index] = {
                                    ...novosPeriodos[index],
                                    mes_final: parseInt(e.target.value)
                                  };
                                  setAcordoForm({...acordoForm, periodos: novosPeriodos});
                                }}
                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none"
                              >
                                <option value={1}>Janeiro</option>
                                <option value={2}>Fevereiro</option>
                                <option value={3}>Março</option>
                                <option value={4}>Abril</option>
                                <option value={5}>Maio</option>
                                <option value={6}>Junho</option>
                                <option value={7}>Julho</option>
                                <option value={8}>Agosto</option>
                                <option value={9}>Setembro</option>
                                <option value={10}>Outubro</option>
                                <option value={11}>Novembro</option>
                                <option value={12}>Dezembro</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label htmlFor="acordo-limite">Limite Mensal (horas) - Opcional</Label>
                  <Input
                    id="acordo-limite"
                    type="number"
                    step="0.1"
                    placeholder="Ex: 10.0"
                    value={acordoForm.limite_mensal_horas}
                    onChange={(e) => setAcordoForm({...acordoForm, limite_mensal_horas: e.target.value})}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Horas extras abaixo deste limite vão para o banco. Acima, seguem a regra definida em "Excedente".
                  </p>
                </div>

                <div className="border-t pt-4">
                  <Label className="text-base font-semibold mb-3 block">
                    Integração com Folha de Pagamento
                  </Label>

                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="acordo-compensacao">Compensação na Folha</Label>
                      <select
                        id="acordo-compensacao"
                        value={acordoForm.compensacao_tipo}
                        onChange={(e) => setAcordoForm({...acordoForm, compensacao_tipo: e.target.value})}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none"
                      >
                        <option value="fim_periodo">Fim do Período (zera saldo ao final)</option>
                        <option value="mensal">Mensal (compensa saldo todo mês na folha)</option>
                      </select>
                      <p className="text-sm text-gray-500 mt-1">
                        Define quando o saldo do banco de horas é pago/descontado na folha.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="acordo-excedente">Horas Excedentes ao Limite Mensal</Label>
                      <select
                        id="acordo-excedente"
                        value={acordoForm.excedente_tipo}
                        onChange={(e) => setAcordoForm({...acordoForm, excedente_tipo: e.target.value})}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none"
                      >
                        <option value="pagar">Pagar na Folha (HE50/HE100)</option>
                        <option value="acumular">Acumular no Banco de Horas</option>
                      </select>
                      <p className="text-sm text-gray-500 mt-1">
                        O que fazer com horas extras que ultrapassam o limite mensal definido acima.
                      </p>
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="acordo-pagar-positivo"
                        checked={acordoForm.pagar_positivo}
                        onChange={(e) => setAcordoForm({...acordoForm, pagar_positivo: e.target.checked})}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <Label htmlFor="acordo-pagar-positivo" className="text-sm font-normal">
                        Pagar saldo positivo ao colaborador na folha
                      </Label>
                    </div>

                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="acordo-descontar-negativo"
                        checked={acordoForm.descontar_negativo}
                        onChange={(e) => setAcordoForm({...acordoForm, descontar_negativo: e.target.checked})}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <Label htmlFor="acordo-descontar-negativo" className="text-sm font-normal">
                        Descontar saldo negativo do colaborador na folha
                      </Label>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button onClick={salvarAcordo}>
                    {editingAcordo ? 'Atualizar' : 'Criar'} Acordo
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAcordoForm(false);
                      setEditingAcordo(null);
                      setAcordoForm({
                        nome: '',
                        descricao: '',
                        convencao_coletiva_id: '',
                        periodo_compensacao_meses: 6,
                        periodos: [
                          { mes_inicial: 1, mes_final: 6 },
                          { mes_inicial: 7, mes_final: 12 }
                        ],
                        limite_mensal_horas: '',
                        compensacao_tipo: 'fim_periodo',
                        excedente_tipo: 'pagar',
                        descontar_negativo: true,
                        pagar_positivo: true
                      });
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        )}

        {/* Aba Jornadas */}
        {allowedTabs.includes('jornadas') && (
        <TabsContent value="jornadas" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Jornadas de Trabalho</CardTitle>
                  <CardDescription>
                    Configure as diferentes jornadas de trabalho
                  </CardDescription>
                </div>
                <Button onClick={() => {
                  // Resetar formulário ao abrir
                  setJornadaForm({
                    nome: '',
                    descricao: '',
                    jornada_semanal: 40.0,
                    horarios: [
                      {
                        id: Date.now().toString(),
                        nome: 'Horário 1',
                        entrada: '08:00',
                        saida: '17:00',
                        intervalo_inicio: '12:00',
                        intervalo_fim: '13:00',
                        horas_calculadas: 8.0
                      }
                    ],
                    dias_horarios: [
                      { dia: 'segunda', horario_id: null, horas: 0 },
                      { dia: 'terca', horario_id: null, horas: 0 },
                      { dia: 'quarta', horario_id: null, horas: 0 },
                      { dia: 'quinta', horario_id: null, horas: 0 },
                      { dia: 'sexta', horario_id: null, horas: 0 },
                      { dia: 'sabado', horario_id: null, horas: 0 },
                      { dia: 'domingo', horario_id: null, horas: 0 }
                    ],
                    horas_diarias: 8.0,
                    dias_semana: ['segunda', 'terca', 'quarta', 'quinta', 'sexta'],
                    tolerancia_minutos: 10,
                    tolerancia_acumulativa: false,
                    max_marcacoes_dia: 12,
                    dia_fechamento_ponto: ''
                  });
                  setEditingJornada(null);
                  setShowJornadaForm(true);
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Jornada
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {jornadas.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nenhuma jornada cadastrada</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {jornadas.map((jornada) => (
                    <div key={jornada.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{jornada.nome}</h3>
                          {jornada.descricao && (
                            <p className="text-gray-600 mt-1 text-sm">{jornada.descricao}</p>
                          )}
                          <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-600">
                            <span className="flex items-center">
                              <Clock className="h-4 w-4 mr-1" />
                              {jornada.horas_diarias}h diárias
                            </span>
                            <span className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {jornada.dias_semana.length} dias/semana
                            </span>
                            {jornada.entrada && jornada.saida && (
                              <>
                                <span className="text-blue-600 font-medium">
                                  {jornada.entrada} → {jornada.saida}
                                </span>
                                {jornada.intervalo_inicio && jornada.intervalo_fim && (
                                  <span className="text-gray-500">
                                    Intervalo: {jornada.intervalo_inicio} - {jornada.intervalo_fim}
                                  </span>
                                )}
                              </>
                            )}
                            <span className="flex items-center text-xs text-gray-400">
                              Tol.: {jornada.tolerancia_minutos || 10}min
                              {jornada.tolerancia_acumulativa ? ' (acum.)' : ''}
                            </span>
                            <span className="flex items-center text-xs text-gray-400">
                              Limite: {jornada.max_marcacoes_dia || 12} batidas/dia
                            </span>
                            <span className="flex items-center text-xs text-gray-400">
                              Fechamento: {jornada.dia_fechamento_ponto ? `dia ${jornada.dia_fechamento_ponto}` : 'fim do mês'}
                            </span>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingJornada(jornada);
                              // Converter jornada antiga para nova estrutura
                              const jornadaConvertida = converterJornadaParaNovaEstrutura(jornada);
                              setJornadaForm(jornadaConvertida);
                              setShowJornadaForm(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => excluirJornada(jornada)}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Modal/Dialog para Jornada (OPÇÃO 1 - Sistema Inteligente) */}
          <Dialog open={showJornadaForm} onOpenChange={setShowJornadaForm}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingJornada ? 'Editar Jornada de Trabalho' : 'Nova Jornada de Trabalho'}
                </DialogTitle>
                <DialogDescription>
                  Sistema inteligente: configure horários e valide automaticamente a carga semanal
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6 py-4">
                {/* Seção 1: Informações Básicas */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">📝 Informações Básicas</h3>
                  
                  <div>
                    <Label htmlFor="jornada-nome">Nome da Jornada *</Label>
                    <Input
                      id="jornada-nome"
                      placeholder="Ex: Comercial 44h"
                      value={jornadaForm.nome}
                      onChange={(e) => setJornadaForm({...jornadaForm, nome: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label htmlFor="jornada-descricao">Descrição</Label>
                    <Input
                      id="jornada-descricao"
                      placeholder="Descrição opcional da jornada"
                      value={jornadaForm.descricao}
                      onChange={(e) => setJornadaForm({...jornadaForm, descricao: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label htmlFor="jornada-semanal">Jornada Semanal (horas) *</Label>
                    <select
                      id="jornada-semanal"
                      value={jornadaForm.jornada_semanal}
                      onChange={(e) => setJornadaForm({...jornadaForm, jornada_semanal: parseFloat(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {[...Array(61)].map((_, i) => (
                        <option key={i} value={i}>{i}h</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Total de horas por semana (0-60h)
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="jornada-tolerancia">Tolerância (minutos)</Label>
                    <Input
                      id="jornada-tolerancia"
                      type="number"
                      min={0}
                      max={60}
                      value={jornadaForm.tolerancia_minutos}
                      onChange={(e) => setJornadaForm({...jornadaForm, tolerancia_minutos: parseInt(e.target.value) || 0})}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Atrasos dentro deste limite não descontam do banco de horas (0-60 min)
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="jornada-max-marcacoes">Limite de marcações por dia</Label>
                    <Input
                      id="jornada-max-marcacoes"
                      type="number"
                      min={4}
                      max={30}
                      step={2}
                      value={jornadaForm.max_marcacoes_dia ?? 12}
                      onChange={(e) => {
                        const valor = Math.min(30, Math.max(4, parseInt(e.target.value, 10) || 4));
                        setJornadaForm({
                          ...jornadaForm,
                          max_marcacoes_dia: valor % 2 === 0 ? valor : valor - 1
                        });
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Limite de segurança para saídas e retornos adicionais. Deve ser par (mínimo 4, máximo 30).
                    </p>
                  </div>

                  <div className="flex items-center gap-3 py-2">
                    <input
                      type="checkbox"
                      id="jornada-acumular"
                      checked={jornadaForm.tolerancia_acumulativa}
                      onChange={(e) => setJornadaForm({...jornadaForm, tolerancia_acumulativa: e.target.checked})}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <Label htmlFor="jornada-acumular" className="text-sm cursor-pointer">
                      Acumular atrasos para tolerância
                    </Label>
                    <span className="text-xs text-gray-500">
                      {jornadaForm.tolerancia_acumulativa
                        ? 'Soma todos atrasos do dia antes de aplicar a tolerância'
                        : 'Tolerância aplicada individualmente por ponto'}
                    </span>
                  </div>

                  <div>
                    <Label htmlFor="jornada-fechamento">Dia de fechamento do ponto</Label>
                    <select
                      id="jornada-fechamento"
                      className="w-full rounded-md border border-input bg-background p-2 text-sm mt-1"
                      value={jornadaForm.dia_fechamento_ponto || ''}
                      onChange={(e) => setJornadaForm({...jornadaForm, dia_fechamento_ponto: e.target.value ? Number(e.target.value) : ''})}
                    >
                      <option value="">Último dia do mês (padrão)</option>
                      {Array.from({ length: 30 }, (_, i) => i + 1).map((dia) => (
                        <option key={dia} value={dia}>Dia {dia}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {jornadaForm.dia_fechamento_ponto
                        ? `O espelho de um mês vai do dia ${Number(jornadaForm.dia_fechamento_ponto) + 1} do mês anterior ao dia ${jornadaForm.dia_fechamento_ponto} do mês de referência.`
                        : 'O espelho de um mês vai do dia 1 ao último dia do mês.'}
                    </p>
                  </div>
                </div>

                {/* Seção 2: Horários de Trabalho (Múltiplos) */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b pb-2">
                    <h3 className="text-sm font-semibold text-gray-700">⏰ Horários de Trabalho</h3>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const novoHorario = {
                          id: Date.now().toString(),
                          nome: `Horário ${jornadaForm.horarios.length + 1}`,
                          entrada: '08:00',
                          saida: '17:00',
                          intervalo_inicio: '12:00',
                          intervalo_fim: '13:00',
                          horas_calculadas: 8.0
                        };
                        setJornadaForm({
                          ...jornadaForm,
                          horarios: [...jornadaForm.horarios, novoHorario]
                        });
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Adicionar Horário
                    </Button>
                  </div>

                  {(jornadaForm.horarios || []).map((horario, index) => {
                    const horasCalc = calcularHorasHorario(
                      horario.entrada,
                      horario.saida,
                      horario.intervalo_inicio,
                      horario.intervalo_fim
                    );
                    
                    return (
                      <div key={horario.id} className="border rounded-lg p-4 space-y-3 bg-gray-50">
                        <div className="flex justify-between items-center">
                          <Input
                            value={horario.nome}
                            onChange={(e) => {
                              const novosHorarios = [...jornadaForm.horarios];
                              novosHorarios[index].nome = e.target.value;
                              setJornadaForm({...jornadaForm, horarios: novosHorarios});
                            }}
                            className="font-medium w-1/2"
                            placeholder="Nome do horário"
                          />
                          {jornadaForm.horarios.length > 1 && (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const novosHorarios = jornadaForm.horarios.filter((_, i) => i !== index);
                                setJornadaForm({...jornadaForm, horarios: novosHorarios});
                              }}
                            >
                              <Trash className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Entrada</Label>
                            <Input
                              type="time"
                              value={horario.entrada}
                              onChange={(e) => {
                                const novosHorarios = [...jornadaForm.horarios];
                                novosHorarios[index].entrada = e.target.value;
                                setJornadaForm({...jornadaForm, horarios: novosHorarios});
                              }}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Saída</Label>
                            <Input
                              type="time"
                              value={horario.saida}
                              onChange={(e) => {
                                const novosHorarios = [...jornadaForm.horarios];
                                novosHorarios[index].saida = e.target.value;
                                setJornadaForm({...jornadaForm, horarios: novosHorarios});
                              }}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Início Intervalo</Label>
                            <Input
                              type="time"
                              value={horario.intervalo_inicio || ''}
                              onChange={(e) => {
                                const novosHorarios = [...jornadaForm.horarios];
                                novosHorarios[index].intervalo_inicio = e.target.value;
                                setJornadaForm({...jornadaForm, horarios: novosHorarios});
                              }}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Fim Intervalo</Label>
                            <Input
                              type="time"
                              value={horario.intervalo_fim || ''}
                              onChange={(e) => {
                                const novosHorarios = [...jornadaForm.horarios];
                                novosHorarios[index].intervalo_fim = e.target.value;
                                setJornadaForm({...jornadaForm, horarios: novosHorarios});
                              }}
                            />
                          </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
                          <span className="text-sm font-semibold text-blue-700">
                            🔒 Horas calculadas: {horasCalc}h/dia
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Seção 3: Dias da Semana com Horários */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">📅 Dias da Semana</h3>
                  <p className="text-xs text-gray-500">
                    Selecione qual horário será aplicado em cada dia
                  </p>
                  
                  <div className="border rounded-lg p-4 space-y-2">
                    {[
                      { id: 'segunda', label: 'Segunda-feira' },
                      { id: 'terca', label: 'Terça-feira' },
                      { id: 'quarta', label: 'Quarta-feira' },
                      { id: 'quinta', label: 'Quinta-feira' },
                      { id: 'sexta', label: 'Sexta-feira' },
                      { id: 'sabado', label: 'Sábado' },
                      { id: 'domingo', label: 'Domingo' }
                    ].map((dia) => {
                      const diaMap = jornadaForm.dias_horarios?.find(d => d.dia === dia.id);
                      const horarioSelecionado = jornadaForm.horarios?.find(h => h.id === diaMap?.horario_id);
                      const horasDia = horarioSelecionado ? calcularHorasHorario(
                        horarioSelecionado.entrada,
                        horarioSelecionado.saida,
                        horarioSelecionado.intervalo_inicio,
                        horarioSelecionado.intervalo_fim
                      ) : 0;
                      
                      return (
                        <div key={dia.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                          <div className="flex items-center space-x-3 flex-1">
                            <input
                              type="checkbox"
                              checked={!!diaMap?.horario_id}
                              onChange={(e) => {
                                if (!jornadaForm.dias_horarios) return;
                                const novosDias = jornadaForm.dias_horarios.map(d => 
                                  d.dia === dia.id 
                                    ? { ...d, horario_id: e.target.checked ? (jornadaForm.horarios?.[0]?.id || null) : null }
                                    : d
                                );
                                setJornadaForm({...jornadaForm, dias_horarios: novosDias});
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700 w-32">{dia.label}</span>
                            
                            {diaMap?.horario_id && (
                              <select
                                value={diaMap.horario_id || ''}
                                onChange={(e) => {
                                  if (!jornadaForm.dias_horarios) return;
                                  const novosDias = jornadaForm.dias_horarios.map(d =>
                                    d.dia === dia.id
                                      ? { ...d, horario_id: e.target.value || null }
                                      : d
                                  );
                                  setJornadaForm({...jornadaForm, dias_horarios: novosDias});
                                }}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Selecione...</option>
                                {(jornadaForm.horarios || []).map(h => (
                                  <option key={h.id} value={h.id}>
                                    {h.nome}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                          
                          <span className={`text-sm font-semibold ml-3 ${horasDia > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                            = {horasDia}h
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Total Semanal com Validação */}
                  {(() => {
                    // Validar se arrays existem
                    if (!jornadaForm.dias_horarios || !jornadaForm.horarios) {
                      return null;
                    }
                    
                    const totalHorasSemana = jornadaForm.dias_horarios.reduce((sum, dia) => {
                      const horario = jornadaForm.horarios.find(h => h.id === dia.horario_id);
                      if (horario) {
                        return sum + calcularHorasHorario(
                          horario.entrada,
                          horario.saida,
                          horario.intervalo_inicio,
                          horario.intervalo_fim
                        );
                      }
                      return sum;
                    }, 0);
                    
                    const diferenca = Math.abs(totalHorasSemana - (jornadaForm.jornada_semanal || 0));
                    const valido = diferenca < 0.1;
                    
                    return (
                      <div className={`mt-4 p-4 rounded-lg border-2 ${
                        valido 
                          ? 'bg-green-50 border-green-500' 
                          : 'bg-red-50 border-red-500'
                      }`}>
                        <div className="flex justify-between items-center">
                          <span className="font-semibold">Total Semanal:</span>
                          <span className="text-lg font-bold">
                            {totalHorasSemana.toFixed(1)}h
                          </span>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <span className="text-sm">Jornada configurada:</span>
                          <span className="text-sm font-medium">
                            {jornadaForm.jornada_semanal}h
                          </span>
                        </div>
                        {valido ? (
                          <div className="mt-2 text-green-700 text-sm font-medium flex items-center">
                            <span className="mr-2">✅</span>
                            Jornada válida!
                          </div>
                        ) : (
                          <div className="mt-2 text-red-700 text-sm font-medium flex items-center">
                            <span className="mr-2">❌</span>
                            Diferença: {diferenca.toFixed(1)}h
                            {totalHorasSemana > jornadaForm.jornada_semanal ? ' a mais' : ' a menos'}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <div className="flex space-x-3 pt-4 border-t">
                  <Button onClick={salvarJornada}>
                    {editingJornada ? 'Atualizar' : 'Criar'} Jornada
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowJornadaForm(false);
                      setEditingJornada(null);
                      setJornadaForm({
                        nome: '', 
                        descricao: '', 
                        horas_diarias: 8.0, 
                        dias_semana: ['segunda', 'terca', 'quarta', 'quinta', 'sexta'],
                        entrada: '08:00',
                        saida: '17:00',
                        intervalo_inicio: '12:00',
                        intervalo_fim: '13:00',
                        tolerancia_minutos: 10,
                        tolerancia_acumulativa: false,
                        max_marcacoes_dia: 12
                      });
                    }}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

        </TabsContent>
        )}

        {/* Aba Saldos dos Funcionários */}
        {allowedTabs.includes('saldos') && (
        <TabsContent value="saldos" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Saldos dos Funcionários</CardTitle>
                  <CardDescription>
                    Visualize e gerencie os saldos de banco de horas
                  </CardDescription>
                </div>
                <div className="space-x-2">
                  <Button onClick={calcularTodos} disabled={loading}>
                    {loading ? (
                      <BarChart3 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <BarChart3 className="h-4 w-4 mr-2" />
                    )}
                    Calcular Todos
                  </Button>
                  <Button variant="outline" onClick={() => carregarDados()}>
                    Atualizar
                  </Button>
                  <Button variant="outline" onClick={exportarSaldosCSV} disabled={saldos.length === 0}>
                    <FileText className="h-4 w-4 mr-1" />
                    Exportar CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {saldos.length === 0 && saldosLoading ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Carregando saldos dos funcionários...</p>
                </div>
              ) : saldos.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Nenhum saldo disponível</p>
                  <p className="text-sm text-gray-500 mt-2">
                    Nenhum funcionário ativo com regime CLT encontrado ou saldo ainda não calculado.
                    Certifique-se de que há funcionários cadastrados com situação "Ativo" e regime "CLT",
                    e que o banco de horas foi calculado.
                  </p>
                  <div className="flex justify-center gap-4 mt-4">
                    <Button onClick={() => carregarDados()}>Atualizar</Button>
                    <Button variant="outline" onClick={() => {
                      const token = localStorage.getItem('token');
                      const headers = { Authorization: `Bearer ${token}` };
                      carregarSaldosFuncionarios(funcionarios, headers);
                    }}>Recarregar Saldos</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Alertas de fechamento */}
                  {alertas.length > 0 && (
                    <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <AlertTriangle className="h-5 w-5 text-amber-600" />
                          <span className="font-semibold text-amber-800">
                            {alertas.length} alerta(s) de fechamento de período
                          </span>
                        </div>
                        <span className="text-xs text-amber-600">Ação do RH necessária</span>
                      </div>
                      <div className="mt-3 space-y-2">
                        {alertas.slice(0, 5).map((alerta, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm bg-white px-3 py-2 rounded border border-amber-200">
                            <div>
                              <span className="font-medium">{alerta.funcionario_nome}</span>
                              {alerta.fim_periodo && (
        <span className="text-amber-700 ml-2">
          Período encerrando — Saldo: {formatarHorasDecimal(alerta.fim_periodo.saldo_atual)}
        </span>
                              )}
                              {alerta.limite_excedido && (
        <span className="text-red-600 ml-2">
          Limite excedido: {formatarHorasDecimal(alerta.limite_excedido.excedente)} acima
        </span>
                              )}
                            </div>
                            <span className="text-xs text-gray-500">{alerta.acordo_nome}</span>
                          </div>
                        ))}
                        {alertas.length > 5 && (
                          <p className="text-xs text-amber-600 text-center">
                            +{alertas.length - 5} alerta(s) não exibido(s)
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Resumo geral */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {saldos.filter(s => s.saldo && !s.erro).length}
                      </div>
                      <div className="text-sm text-gray-600">Com Saldo</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {saldos.filter(s => s.saldo?.saldo_acumulado_horas > 0).length}
                      </div>
                      <div className="text-sm text-gray-600">Crédito</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {saldos.filter(s => s.saldo?.saldo_acumulado_horas < 0).length}
                      </div>
                      <div className="text-sm text-gray-600">Débito</div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-gray-600">
                        {saldos.filter(s => s.erro || !s.saldo).length}
                      </div>
                      <div className="text-sm text-gray-600">Sem Dados</div>
                    </div>
                  </div>

                  {/* Lista detalhada de funcionários */}
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {saldos.map((item, index) => (
                      <div
                        key={index}
                        className={`border rounded-lg p-4 ${
                          item.erro 
                            ? 'bg-red-50 border-red-200' 
                            : item.saldo?.saldo_acumulado_horas > 0 
                            ? 'bg-green-50 border-green-200'
                            : item.saldo?.saldo_acumulado_horas < 0
                            ? 'bg-red-50 border-red-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div>
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-lg">
                                {item.funcionario.nome || item.funcionario.name || 'Nome não informado'}
                              </div>
                              <div className="text-sm text-gray-600">
                                {item.empresa ? (
                                  <span className="text-blue-600 font-medium">{item.empresa.name}</span>
                                ) : (
                                  <span className="text-gray-400">Sem empresa vinculada</span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100">
                                  {item.acordoNome || 'Sem acordo'}
                                  {item.herdado && item.acordoNome && <span className="ml-1 text-blue-500" title="Herdado da empresa">↳</span>}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100">
                                  {item.jornadaNome || 'Sem jornada'}
                                  {item.herdado && item.jornadaNome && <span className="ml-1 text-blue-500" title="Herdado da empresa">↳</span>}
                                </span>
                              </div>
                              
                              {item.erro ? (
                                <div className="text-sm text-red-600 mt-2">
                                  ❌ Erro: {item.erro}
                                </div>
                              ) : item.saldo ? (
                                <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-600">Saldo Atual:</span>
        <span className={`ml-2 font-semibold ${
          item.saldo.saldo_acumulado_horas > 0 ? 'text-green-600' :
          item.saldo.saldo_acumulado_horas < 0 ? 'text-red-600' : 'text-gray-600'
        }`}>
          {formatarHorasDecimal(item.saldo.saldo_acumulado_horas)}
        </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Período:</span>
                                    <span className="ml-2">{item.saldo.periodo_referencia || item.saldo.mes_referencia || 'N/A'}</span>
                                  </div>
                                  <div>
    <span className="text-gray-600">Saldo Mensal:</span>
    <span className="ml-2">{formatarHorasDecimal(item.saldo.saldo_mensal_horas)}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600">Status:</span>
                                    <span className={`ml-2 ${item.saldo.status === 'CALCULADO' ? 'text-green-600' : 'text-orange-600'}`}>
                                      {item.saldo.status || 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500 mt-2">
                                  ⚠️ Sem dados de banco de horas
                                </div>
                              )}
                            </div>
                            
                            <div className="flex flex-col space-y-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => calcularBancoHoras(item.funcionario.id)}
                                disabled={loading}
                              >
                                <BarChart3 className="h-3 w-3 mr-1" />
                                Calcular
                              </Button>
                              
                              {!item.erro && item.saldo && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => toggleExpandir(item.funcionario.id)}
                                  disabled={loading}
                                >
                                  {funcionarioExpandido === item.funcionario.id ? (
                                    <ChevronUp className="h-3 w-3 mr-1" />
                                  ) : (
                                    <ChevronDown className="h-3 w-3 mr-1" />
                                  )}
                                  Detalhes
                                </Button>
                              )}
                              
                              {(() => {
                                const temAcordo = !!(item.funcionario.acordo_banco_horas_id || item.empresa?.acordo_banco_horas_id);
                                const temJornada = !!(item.funcionario.jornada_id || item.empresa?.jornada_id);
                                const herdado = !item.funcionario.acordo_banco_horas_id || !item.funcionario.jornada_id;
                                if (temAcordo && temJornada) {
                                  return (
                                    <div className={`text-xs text-center ${herdado ? 'text-blue-600' : 'text-green-600'}`}>
                                      {herdado ? '↳ Herdado' : '✓ Próprio'}
                                    </div>
                                  );
                                }
                                return (
                                  <div className="text-xs text-orange-600 text-center">
                                    ⚠️ Sem config.
                                  </div>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Detalhamento Mensal - Exibir quando expandido */}
                          {funcionarioExpandido === item.funcionario.id && detalhamentoMensal[item.funcionario.id] && (
                            <div className="mt-4 border-t pt-4">
                              <h4 className="font-semibold text-sm mb-3 text-gray-700">
                                📊 Detalhamento Mês a Mês
                              </h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-gray-100 border-b">
                                      <th className="px-3 py-2 text-left">Mês</th>
                                      <th className="px-3 py-2 text-right">Trabalhadas</th>
                                      <th className="px-3 py-2 text-right">Devidas</th>
                                      <th className="px-3 py-2 text-right">Saldo Mês</th>
                                      <th className="px-3 py-2 text-right">Saldo Acum.</th>
                                      <th className="px-3 py-2 text-right"></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detalhamentoMensal[item.funcionario.id].map((mes, idx) => {
                                      const mesKey = `${item.funcionario.id}-${mes.mes}-${mes.ano}`;
                                      const expandido = mesExpandido === mesKey;
                                      return (
                                      <React.Fragment key={idx}>
                                        <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => setMesExpandido(expandido ? null : mesKey)}>
                                          <td className="px-3 py-2 font-medium">
                                            {mes.mes_nome}/{mes.ano}
                                          </td>
                                          <td className="px-3 py-2 text-right text-blue-600">
                                            {formatarHorasDecimal(mes.horas_trabalhadas)}
                                          </td>
                                          <td className="px-3 py-2 text-right text-gray-600">
                                            {formatarHorasDecimal(mes.horas_devidas)}
                                          </td>
                                          <td className={`px-3 py-2 text-right font-semibold ${
                                            mes.saldo_mensal > 0 ? 'text-green-600' :
                                            mes.saldo_mensal < 0 ? 'text-red-600' : 'text-gray-600'
                                          }`}>
                                            {formatarHorasDecimal(mes.saldo_mensal)}
                                          </td>
                                          <td className={`px-3 py-2 text-right font-bold ${
                                            mes.saldo_acumulado > 0 ? 'text-green-600' :
                                            mes.saldo_acumulado < 0 ? 'text-red-600' : 'text-gray-600'
                                          }`}>
                                            {formatarHorasDecimal(mes.saldo_acumulado)}
                                          </td>
                                          <td className="px-3 py-2 text-right text-gray-400">
                                            {expandido ? '▲' : '▼'}
                                          </td>
                                        </tr>
                                        {expandido && mes.dias && mes.dias.length > 0 && (
                                          <tr>
                                            <td colSpan={6} className="px-6 py-2 bg-gray-50">
                                              <table className="w-full text-xs">
                                                <thead>
                                                  <tr className="text-gray-500 border-b">
                                                    <th className="px-2 py-1 text-left">Data</th>
                                                    <th className="px-2 py-1 text-left">Dia</th>
                                                    <th className="px-2 py-1 text-left">Jornada</th>
                                                    <th className="px-2 py-1 text-left">Marcações</th>
                                                    <th className="px-2 py-1 text-right">Trab.</th>
                                                    <th className="px-2 py-1 text-right">Saldo</th>
                                                    <th className="px-2 py-1 text-left">Status</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {mes.dias.map((d, di) => (
                                                    <tr key={di} className="border-b last:border-0 hover:bg-white">
                                                      <td className="px-2 py-1 whitespace-nowrap">{d.data}</td>
                                                      <td className="px-2 py-1 text-gray-500">{d.dia_semana || '-'}</td>
                                                      <td className="px-2 py-1 text-gray-500">{d.jornada_prevista || '-'}</td>
                                                      <td className="px-2 py-1 font-mono text-gray-600">
                                                        {Array.isArray(d.marcacoes) ? d.marcacoes.join(' · ') : '-'}
                                                      </td>
                                                      <td className="px-2 py-1 text-right font-medium">{formatarHorasDecimal(d.horas_trabalhadas)}</td>
                                                      <td className={`px-2 py-1 text-right font-medium ${
                                                        (d.debito_credito_minutos || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                                                      }`}>
                                                        {formatarHorasDecimal((d.debito_credito_minutos || 0) / 60)}
                                                      </td>
                                                      <td className="px-2 py-1">
                                                        <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                                                          d.status === 'ok' ? 'bg-green-100 text-green-700' :
                                                          d.status === 'falta' ? 'bg-red-100 text-red-700' :
                                                          d.status === 'divergencia' ? 'bg-amber-100 text-amber-700' :
                                                          'bg-gray-100 text-gray-600'
                                                        }`}>
                                                          {d.status || '-'}
                                                        </span>
                                                      </td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>


                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Aba Relatórios */}
        {allowedTabs.includes('relatorios') && (
        <TabsContent value="relatorios" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                  <CardTitle>Relatórios de Banco de Horas</CardTitle>
                  <CardDescription>
                    Dashboard completo com dados do Fluxo B
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const mes = document.getElementById('rel-mes')?.value || new Date().getMonth() + 1;
                      const ano = document.getElementById('rel-ano')?.value || new Date().getFullYear();
                      const empresa = document.getElementById('rel-empresa')?.value || '';
                      const params = { mes, ano };
                      if (empresa) params.empresa_id = empresa;
                      const response = await axios.get(`${API}/api/timetracking/relatorios/banco-horas-completo`, {
                        headers: { Authorization: `Bearer ${token}` }, params
                      });
                      const data = response.data;
                      // Trigger PDF generation via backend
                      const pdfRes = await axios.post(`${API}/api/timetracking/relatorios/banco-horas/pdf`,
                        { dados: { resultados: data.resultados, periodo: data.periodo, gerado_por: user?.name } },
                        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
                      );
                      const url = window.URL.createObjectURL(new Blob([pdfRes.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `banco_horas_${mes}_${ano}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast({ title: "Sucesso!", description: "PDF gerado com sucesso!" });
                    } catch (e) {
                      toast({ title: "Erro", description: "Erro ao gerar PDF", variant: "destructive" });
                    }
                  }}>
                    <FileText className="h-4 w-4 mr-1" />
                    Exportar PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const mes = document.getElementById('rel-mes')?.value || new Date().getMonth() + 1;
                      const ano = document.getElementById('rel-ano')?.value || new Date().getFullYear();
                      const empresa = document.getElementById('rel-empresa')?.value || '';
                      const params = { mes, ano };
                      if (empresa) params.empresa_id = empresa;
                      const response = await axios.get(`${API}/api/timetracking/relatorios/exportar-csv`, {
                        headers: { Authorization: `Bearer ${token}` }, params, responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `banco_horas_${mes}_${ano}.csv`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      toast({ title: "Sucesso!", description: "CSV exportado com sucesso!" });
                    } catch (e) {
                      toast({ title: "Erro", description: "Erro ao exportar CSV", variant: "destructive" });
                    }
                  }}>
                    <BarChart3 className="h-4 w-4 mr-1" />
                    Exportar CSV
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <RelatoriosDashboard
                acordos={acordos}
                empresas={empresas}
                API={API}
                user={user}
                toast={toast}
              />
            </CardContent>
          </Card>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

// ────────────────── Componente Interno: RelatoriosDashboard ──────────────────
const RelatoriosDashboard = ({ acordos, empresas, API, user, toast }) => {
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [empresaId, setEmpresaId] = useState('');
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);

  const carregarRelatorio = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { mes, ano };
      if (empresaId) params.empresa_id = empresaId;
      const response = await axios.get(`${API}/api/timetracking/relatorios/banco-horas-completo`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setDados(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      toast({ title: "Erro", description: "Erro ao carregar relatório", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarRelatorio();
  }, [mes, ano, empresaId]);

  // Agregar totais
  let totalFunc = 0, totalCredores = 0, totalDevedores = 0, saldoGeral = 0;
  let alertasPeriodo = [];
  if (dados?.resultados) {
    dados.resultados.forEach(r => {
      const res = r.resumo || {};
      totalFunc++;
      const sm = parseFloat(res.saldo_mensal_horas || 0);
      if (sm > 0) totalCredores++;
      else if (sm < 0) totalDevedores++;
      saldoGeral += sm;
      if (res.aviso_fim_periodo) alertasPeriodo.push(r);
    });
  }

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div>
          <Label className="text-xs">Mês</Label>
          <select
            id="rel-mes"
            value={mes}
            onChange={e => setMes(parseInt(e.target.value))}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none"
          >
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'][i]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Ano</Label>
          <select
            id="rel-ano"
            value={ano}
            onChange={e => setAno(parseInt(e.target.value))}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none"
          >
            {[2024, 2025, 2026, 2027, 2028].map(a => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Empresa</Label>
          <select
            id="rel-empresa"
            value={empresaId}
            onChange={e => setEmpresaId(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none"
          >
            <option value="">Todas</option>
            {empresas.map(e => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <Button onClick={carregarRelatorio} disabled={loading} className="w-full">
            {loading ? 'Carregando...' : 'Atualizar'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Carregando relatório...</div>
      ) : !dados ? (
        <div className="text-center py-12 text-gray-500">Nenhum dado encontrado</div>
      ) : (
        <>
          {/* Cards de resumo */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{totalFunc}</div>
              <div className="text-xs text-gray-600">Funcionários</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{totalCredores}</div>
              <div className="text-xs text-gray-600">Crédito</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">{totalDevedores}</div>
              <div className="text-xs text-gray-600">Débito</div>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-amber-600">{alertasPeriodo.length}</div>
              <div className="text-xs text-gray-600">Alertas</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className={`text-2xl font-bold ${saldoGeral >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {saldoGeral.toFixed(1)}h
              </div>
              <div className="text-xs text-gray-600">Saldo Geral</div>
            </div>
          </div>

          {/* Alertas de período */}
          {alertasPeriodo.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <span className="font-semibold text-amber-800">Períodos encerrando — ação do RH necessária</span>
              </div>
              <div className="space-y-2">
                {alertasPeriodo.map((a, i) => (
                  <div key={i} className="text-sm bg-white px-3 py-2 rounded border border-amber-200 flex justify-between">
                    <span className="font-medium">{a.funcionario_nome}</span>
                    <span className="text-amber-700">Saldo: {a.resumo?.saldo_acumulado?.toFixed(2)}h</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabela de funcionários */}
          <div className="border rounded-lg overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left">Funcionário</th>
                    <th className="px-3 py-2 text-left">Cargo</th>
                    <th className="px-3 py-2 text-right">Dias</th>
                    <th className="px-3 py-2 text-right">H. Trab.</th>
                    <th className="px-3 py-2 text-right">Saldo Ant.</th>
                    <th className="px-3 py-2 text-right">Saldo Mês</th>
                    <th className="px-3 py-2 text-right">Saldo Acum.</th>
                    <th className="px-3 py-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.resultados.map((r, i) => {
                    const res = r.resumo || {};
                    const sm = parseFloat(res.saldo_mensal_horas || 0);
                    const sa = parseFloat(res.saldo_acumulado || 0);
                    const corSm = sm > 0 ? 'text-green-600' : sm < 0 ? 'text-red-600' : 'text-gray-600';
                    const corSa = sa > 0 ? 'text-green-600 font-bold' : sa < 0 ? 'text-red-600 font-bold' : 'text-gray-600';
                    return (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium">{r.funcionario_nome}</td>
                        <td className="px-3 py-2 text-gray-600">{r.cargo}</td>
                        <td className="px-3 py-2 text-right">{res.dias_trabalhados || 0}</td>
                        <td className="px-3 py-2 text-right">{res.total_horas_trabalhadas?.toFixed(1)}h</td>
                        <td className="px-3 py-2 text-right">{res.saldo_anterior?.toFixed(2)}h</td>
                        <td className={`px-3 py-2 text-right ${corSm}`}>{sm.toFixed(2)}h</td>
                        <td className={`px-3 py-2 text-right ${corSa}`}>{sa.toFixed(2)}h</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            sm > 0 ? 'bg-green-100 text-green-700' :
                            sm < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {sm > 0 ? 'Crédito' : sm < 0 ? 'Débito' : 'Equilíbrio'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-xs text-gray-400 text-right">
            Fonte: Fluxo B (fonte única da verdade) | {dados.resultados.length} funcionários
          </div>
        </>
      )}
    </div>
  );
};

export default BancoHorasPage;
